import { useQuery } from '@tanstack/react-query';
import type { WorkbookSource } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import type { SourcePreviewData } from './WorkbookSourcePreview';

/**
 * How long a loaded Source preview is reused. Every panel on a Source (its
 * preview, a Worksheet or pivot reading it) shares the one query; without
 * this each panel opening refetches it, and a Source read ties up the
 * server while it runs.
 */
export const SOURCE_PREVIEW_STALE_MS = 60_000;

/** The one query of a Source's preview, shared by every panel reading it. */
export function sourcePreviewQuery(workbookId: string, source: WorkbookSource) {
  return orpc.workbooks.source.queryOptions({
    input: { workbookId, source },
    staleTime: SOURCE_PREVIEW_STALE_MS,
  });
}

/** A preview with its true row count once read, in place of its estimate. */
export function withTrueRowCount(
  preview: SourcePreviewData | undefined,
  rowCount: number | undefined,
): SourcePreviewData | undefined {
  if (!preview?.rowCountEstimated || rowCount === undefined) return preview;
  return { ...preview, rowCount, rowCountEstimated: false };
}

/**
 * A Source's preview, which reads only the rows it shows; when its row count
 * is an estimate (a Large file not yet counted), its true count is read
 * after, and replaces it.
 */
export function useSourcePreview(workbookId: string, source: WorkbookSource) {
  const input = { workbookId, source };
  const table = useQuery(sourcePreviewQuery(workbookId, source));
  const count = useQuery({
    ...orpc.workbooks.sourceRowCount.queryOptions({ input }),
    enabled: Boolean(table.data?.rowCountEstimated),
  });
  return {
    data: withTrueRowCount(table.data, count.data?.rowCount),
    error: table.error,
    isFetching: table.isFetching,
  };
}
